
export enum WorkflowStatus {
  CREATED = 'created',
  RUNNING = 'running',
  COMPLETED = 'completed',
  FAILED = 'failed',
}

export interface WorkflowSummary {
  id: string;
  name: string;
  status: WorkflowStatus;
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  progress?: {
    percentage: number;
    summary: string;
  };
  hasWarnings: boolean;
  hasErrors: boolean;
  correlationId: string;
}

export interface Workflow extends WorkflowSummary {
  input: unknown;
}

export interface WorkflowLogEntry {
  id: string;
  level: 'info' | 'warn' | 'error';
  correlationId: string;
  message: string;
  timestamp: string;
  payload: Record<string, unknown>;
  workflowId: string;
}

export type CreateWorkflowCommand =
  | {
    name: 'bulk-shipments-void',
    input: {
      reason: string;
      shipments: {
        id: string;
        displayName?: string;
      }[];
    };
  };

export interface GetWorkflowsQuery {
  limit?: number;
  offset?: number;
  updatedAfter?: string;
  createdBefore?: string;
}

export interface WorkflowsQueryResults {
  workflows: WorkflowSummary[];
  count: number;
  limit: number;
  offset: number;
  totalCount: number;
}

export interface WorkflowLogQuery {
  limit?: number;
  offset?: number;
  createdAfter?: string;
  createdBefore?: string;
}

export interface WorkflowLogQueryResults {
  entries: WorkflowLogEntry[];
  count: number;
  limit: number;
  offset: number;
  totalCount: number;
}

export interface WorkflowsService {
  createWorkflow(command: CreateWorkflowCommand): Promise<Workflow>;
  getWorkflows(query: GetWorkflowsQuery): Promise<WorkflowsQueryResults>;
  getWorkflow(id: string): Promise<Workflow>;
  getWorkflowLog(workflowId: string, query?: WorkflowLogQuery): Promise<WorkflowLogQueryResults>;
}

export type UseWorkflowsHook = () => {
  workflows?: WorkflowSummary[];
  isLoading: boolean;
  totalCount: number;
  error?: Error;
  loadMore: () => void;
  update: () => void;
};

export type UseWorkflowHook = (id: string) => {
  workflow?: Workflow;
  isLoading: boolean;
  error?: Error;
  update: () => void;
};

export type UseCreateWorkflowHook = () => {
  execute: (command: CreateWorkflowCommand) => void;
  workflow?: Workflow;
  isLoading: boolean;
  error?: Error;
};

export type UseWorkflowLogHook = (id: string) => {
  log?: WorkflowLogEntry[];
  isLoading: boolean;
  error?: Error;
  totalCount: number;
  loadMore: () => void;
  update: () => void;
};
